import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import CanvasJSReact from '../charts/canvasjs.react';
import FusionCharts from 'fusioncharts';
import TimeSeries from 'fusioncharts/fusioncharts.timeseries';
import ReactFC from 'react-fusioncharts';
import { Message } from 'rsuite';
import { colors } from '../../../../ui';

// Initialize FusionCharts
FusionCharts.options.license({
  key: process.env.REACT_APP_FUSION_KEY,
  creditLabel: false,
});

ReactFC.fcRoot(FusionCharts, TimeSeries);

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const ChartRenderer = ({
  chartData,
  canvasConfig,
  fusionConfig,
  chartLibrary = 'canvas',
  darkMode = false,
  height = '400px',
  className,
  ...props
}) => {
  const chartRef = useRef(null);
  const [renderError, setRenderError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    setRenderError(null);

    // Simulate loading delay for better UX
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [chartData, chartLibrary]);

  const handleChartError = (error) => {
    console.error('Chart rendering error:', error);
    setRenderError(error.message || 'Failed to render chart');
    setIsLoading(false);
  };

  if (renderError) {
    return (
      <ErrorContainer>
        <Message type="error" header="Chart Rendering Error">
          {renderError}
        </Message>
      </ErrorContainer>
    );
  }

  if (isLoading) {
    return (
      <LoadingContainer height={height}>
        <LoadingSpinner />
        <LoadingText>Rendering chart...</LoadingText>
      </LoadingContainer>
    );
  }

  return (
    <ChartContainer className={className} height={height} {...props}>
      {chartLibrary === 'canvas' ? (
        <CanvasChartWrapper>
          {canvasConfig && (
            <CanvasJSChart
              options={canvasConfig}
              onRef={(ref) => (chartRef.current = ref)}
              containerProps={{
                width: '100%',
                height: height
              }}
            />
          )}
        </CanvasChartWrapper>
      ) : (
        <FusionChartWrapper>
          {fusionConfig && (
            <ReactFC
              {...fusionConfig}
              onError={handleChartError}
              onRender={() => setIsLoading(false)}
            />
          )}
        </FusionChartWrapper>
      )}
    </ChartContainer>
  );
};

// Styled Components
const ChartContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '400px'};
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background: white;
`;

const CanvasChartWrapper = styled.div`
  width: 100%;
  height: 100%;

  /* Custom CanvasJS styling overrides */
  .canvasjs-chart-canvas {
    border-radius: 8px;
  }

  .canvasjs-chart-tooltip {
    border-radius: 6px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    border: none !important;
    font-size: 12px !important;
  }

  .canvasjs-chart-legend {
    font-size: 12px !important;
  }

  .canvasjs-chart-legend > div {
    margin: 2px !important;
  }
`;

const FusionChartWrapper = styled.div`
  width: 100%;
  height: 100%;

  /* Custom FusionCharts styling overrides */
  .fusioncharts-container {
    border-radius: 8px;
    overflow: hidden;
  }
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '400px'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${colors.neutral[50]};
  border-radius: 8px;
  border: 2px dashed ${colors.neutral[200]};
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: ${colors.neutral[600]};
  font-size: 13px;
`;

const ErrorContainer = styled.div`
  width: 100%;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default ChartRenderer;
